/**
 * LoanTabHeader — tier 1 of the Drilldown Tab Header Standard.
 *
 * The page header. It lives OUTSIDE the card, directly on the page background:
 * no card, no border, no shadow, no background fill. That is what makes it read
 * as page-level rather than as one more card among peers, and it's the single
 * anti-pattern this component exists to prevent.
 *
 *   ┌ PAGE HEADER ─────────────────────── [Primary action] ┐   ← no surface
 *   │  Title                                                │
 *   │  Subtitle                                             │
 *   └───────────────────────────────────────────────────────┘
 *                        ↕ 18px  (owned by LoanTabShell)
 *   [ KPI strip — bare tiles, never wrapped in a card ]
 *
 * What belongs here vs. in a card header, when you're unsure:
 *
 *   > Does this control change what's inside ONE card, or does it change the
 *   > page / create a record?
 *   >   changes one card  → section header (LoanSectionCard `controls`)
 *   >   changes the page  → page header    (`primaryAction`)
 *
 * One primary action per tab, maximum. If a tab seems to need two, one of them
 * is section-scoped. A tab with no primary action renders the title block alone
 * and leaves the right side empty — don't invent an action to fill it.
 *
 * Accessibility: the title is the tab panel's <h1> and takes focus on mount
 * (tabindex -1, no scroll jump) so keyboard and screen-reader users land at the
 * top of the new content when they switch tabs.
 */
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanTabKpiStrip from "./LoanTabKpiStrip";
import { TabGhostButton, TabPrimaryButton } from "./tabButtons";
import { TAB_TOKENS as T } from "./tabTokens";

/** Height of the drilldown tab bar — what a sticky header pins beneath. */
const TAB_BAR_OFFSET = 0;

const renderAction = (action, Component, key) => {
  if (!action) return null;
  // Already an element — the caller wanted full control.
  if (action.type || action.$$typeof) return action;
  return (
    <Component
      key={key}
      label={action.label}
      icon={action.icon}
      loading={action.loading}
      disabled={action.disabled}
      onClick={action.onClick}
      sx={action.sx}
    />
  );
};

const LoanTabHeader = ({
  title,
  subTitle,
  subTitleContent,
  eyebrow,
  meta,
  primaryAction,
  secondaryActions,
  endContent,
  kpis,
  bottomContent,
  sticky = false,
  autoFocusTitle = true,
  titleId,
  sx,
}) => {
  const titleRef = useRef(null);
  const sentinelRef = useRef(null);
  const [pinned, setPinned] = useState(false);

  // §12 — on tab change, move focus to the page title so keyboard and
  // screen-reader users land at the top of the new content. `preventScroll`
  // keeps it from yanking the viewport.
  useEffect(() => {
    if (!autoFocusTitle) return;
    titleRef.current?.focus?.({ preventScroll: true });
  }, [autoFocusTitle, title]);

  // §11 — the pinned border/shadow appear only once the header has actually
  // detached, via a sentinel above it rather than a scroll listener.
  useEffect(() => {
    if (!sticky || !sentinelRef.current) return undefined;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      { threshold: 1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sticky]);

  const actions = useMemo(() => {
    const list = [];
    (secondaryActions || []).forEach((a, i) =>
      list.push(renderAction(a, TabGhostButton, `secondary-${i}`)),
    );
    const primary = renderAction(primaryAction, TabPrimaryButton, "primary");
    if (primary) list.push(primary);
    if (endContent) list.push(<Box key="end">{endContent}</Box>);
    return list.filter(Boolean);
  }, [primaryAction, secondaryActions, endContent]);

  const rootStyles = useMemo(
    () => ({
      width: "100%",
      // Explicitly NO surface. Listed rather than omitted so a future edit has
      // to argue with the standard instead of quietly adding a card.
      background: sticky ? T.color.bg : "transparent",
      border: "none",
      boxShadow: "none",
      ...(sticky && {
        position: "sticky",
        top: TAB_BAR_OFFSET,
        zIndex: 10,
        paddingBlock: "12px",
        transition: "box-shadow 150ms ease, border-color 150ms ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        ...(pinned && {
          borderBottom: `1px solid ${T.color.border}`,
          boxShadow: T.shadow.pinned,
        }),
      }),
      ...sx,
    }),
    [sticky, pinned, sx],
  );

  if (!title && !bottomContent && !kpis?.length) return null;

  return (
    <>
      {sticky && <Box ref={sentinelRef} sx={{ height: "1px" }} />}
      <Box component="header" sx={rootStyles}>
        <Flx
          wrap
          fw
          jb
          sx={{
            gap: T.space.controlGap,
            alignItems: "flex-start",
            [T.bp.headerStack]: { flexDirection: "column", gap: "12px" },
          }}
        >
          {/* ── Title block ── */}
          <Flx column sx={{ gap: T.space.titleGap, minWidth: 0 }}>
            {eyebrow && (
              <Txt sx={{ ...T.type.kpiLabel, fontSize: "9.5px" }}>{eyebrow}</Txt>
            )}
            <Flx ac wrap sx={{ gap: "10px", minWidth: 0 }}>
              {/* Box, not Txt — Txt doesn't forward refs, and §12 needs one to
                  move focus here on tab change. */}
              <Box
                ref={titleRef}
                component="h1"
                id={titleId}
                tabIndex={-1}
                sx={{
                  ...T.type.pageTitle,
                  margin: 0,
                  // The title never shrinks below 19px — it stays the heaviest
                  // thing on the page at every width, which is what preserves
                  // the anchor.
                  [T.bp.twoUp]: { fontSize: "19px" },
                  "&:focus": { outline: "none" },
                  "&:focus-visible": {
                    outline: `2px solid ${T.color.brand}`,
                    outlineOffset: "2px",
                    borderRadius: "4px",
                  },
                }}
              >
                {title}
              </Box>
              {meta}
            </Flx>
            {subTitle && <Txt sx={T.type.pageSubtitle}>{subTitle}</Txt>}
            {/* Sits in the title block, so it hugs the subtitle instead of
                taking the 18px header→content gap (a legend, a key). */}
            {subTitleContent && <Box sx={{ mt: "6px" }}>{subTitleContent}</Box>}
          </Flx>

          {/* ── Actions ── right-aligned, baseline with the title.
              Below 720px the header stacks and the action goes full-width. */}
          {actions.length > 0 && (
            <Flx
              ac
              sx={{
                gap: T.space.actionGap,
                flexShrink: 0,
                [T.bp.headerStack]: {
                  width: "100%",
                  "& > *": { flex: 1 },
                  "& .MuiButton-root": { width: "100%" },
                },
              }}
            >
              {actions}
            </Flx>
          )}
        </Flx>

        {/* ── KPI strip — bare tiles, 18px below the header block ── */}
        {kpis?.length > 0 && (
          <Box sx={{ mt: T.space.headerGap }}>
            <LoanTabKpiStrip items={kpis} />
          </Box>
        )}

        {bottomContent && <Box sx={{ mt: T.space.headerGap }}>{bottomContent}</Box>}
      </Box>
    </>
  );
};

LoanTabHeader.propTypes = {
  /** The tab's name. Sentence case. Renders as the tab panel's <h1>. */
  title: PropTypes.node,
  /** One line. Sentence case. */
  subTitle: PropTypes.node,
  /** Small supporting content (a legend, a key) directly under the subtitle. */
  subTitleContent: PropTypes.node,
  /** Small uppercase label above the title. Use sparingly. */
  eyebrow: PropTypes.node,
  /** Status chip or count rendered inline after the title — never an action. */
  meta: PropTypes.node,
  /** The tab's single primary action: {label, onClick, icon, loading, disabled} or an element. */
  primaryAction: PropTypes.oneOfType([PropTypes.object, PropTypes.element]),
  /** Ghost buttons left of the primary. Only valid when a primary exists; cap the total at three. */
  secondaryActions: PropTypes.array,
  /** Escape hatch for a control that isn't a button (a selector, a switcher). */
  endContent: PropTypes.node,
  /** 4–6 bare KPI tiles. Omit entirely when aggregates don't materially help. */
  kpis: PropTypes.array,
  /** Anything else that belongs above the first card. */
  bottomContent: PropTypes.node,
  /** Pin the header on scroll. Opt-in: worth it on long tabs, noise on short ones. */
  sticky: PropTypes.bool,
  /** Move focus to the title on mount/title change. On by default (§12). */
  autoFocusTitle: PropTypes.bool,
  /** id for the <h1>, for aria-labelledby on the tab panel. */
  titleId: PropTypes.string,
  sx: PropTypes.object,
};

export default memo(LoanTabHeader);
